import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { authService } from "@/services/authService";
import type { ExtensionMessenger } from "@/model/interface/ExtensionBridge";

type HandoffStatus = "checking" | "connecting" | "done" | "need-login" | "no-extension" | "error";

function getExtensionRuntime(): ExtensionMessenger["runtime"] | undefined {
  return (window as unknown as { chrome?: ExtensionMessenger }).chrome?.runtime;
}

export default function ExtensionAuth() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<HandoffStatus>("checking");
  const extensionId = searchParams.get("ext");

  const attemptHandoff = useCallback(async () => {
    const runtime = getExtensionRuntime();
    if (!extensionId || !runtime?.sendMessage) {
      setStatus("no-extension");
      return;
    }
    const accessToken = await authService.getAccessToken();
    const refreshToken = await authService.getRefreshToken();
    if (!accessToken) {
      setStatus("need-login");
      return;
    }
    setStatus("connecting");
    runtime.sendMessage(
      extensionId,
      { type: "RETREEVO_AUTH", accessToken, refreshToken },
      () => {
        if (runtime.lastError) {
          setStatus("error");
          return;
        }
        setStatus("done");
      },
    );
  }, [extensionId]);

  useEffect(() => {
    attemptHandoff();
  }, [attemptHandoff]);

  useEffect(() => {
    function onStorage() {
      attemptHandoff();
    }
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [attemptHandoff]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center text-foreground">
      <h1 className="text-xl font-semibold">Connect Retreevo</h1>
      {status === "checking" || status === "connecting" ? (
        <p className="text-muted-foreground">Connecting the extension...</p>
      ) : null}
      {status === "done" ? (
        <p className="text-muted-foreground">
          Connected. You can close this tab and return to the extension.
        </p>
      ) : null}
      {status === "need-login" ? (
        <>
          <p className="text-muted-foreground">
            Sign in to Retreevo, then reopen Connect from the extension.
          </p>
          <Button asChild>
            <Link to="/login">Sign in</Link>
          </Button>
        </>
      ) : null}
      {status === "no-extension" ? (
        <p className="text-muted-foreground">Open this page from the Retreevo Capture extension.</p>
      ) : null}
      {status === "error" ? (
        <p className="text-muted-foreground">
          Could not reach the extension. Make sure it is installed, then try again.
        </p>
      ) : null}
    </div>
  );
}
